import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import useMakeRequest from '../../utils/ApiHelper';
import { decodeID } from '../../utils/Helper';
import { ApiConstants } from '../../constants/ApiConstants';
import { LanguageConstants } from '../../constants/LanguageConstants';
import FrontLoader from '../layout/FrontLoader';
import ProImg from '../../Assets/images/listing-bnr.webp';
import './ProductDetails.scss';

const ProductDetails = () => {
  const { id } = useParams();
  const productDetail = useMakeRequest();
  const [images, setImages] = useState<any[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [id]);

  useEffect(() => {
    if (decodeID(id)) {
      const payload = {
        detail_id: decodeID(id),
      };
      productDetail.makeRequest({
        payload,
        url: ApiConstants?.Prodcutdetails,
        toastStatus: false,
      });
    }
  }, [id]);

  useEffect(() => {
    if (productDetail.status) {
      setImages(productDetail?.data?.data?.product_image_detail ?? []);
    }
  }, [productDetail.status]);

  const product = productDetail?.data?.data?.product_details;
  const productName = product?.name?.trim() || '—';
  const productDescription = product?.description?.trim() || '';

  const gallerySlides = useMemo(() => {
    const rows = Array.isArray(images) ? images : [];
    if (rows.length > 0) {
      return rows.map((row: { file_path?: string }, index: number) => ({
        key: row?.file_path ?? `slide-${index}`,
        src: row?.file_path || ProImg,
      }));
    }
    return [{ key: 'placeholder', src: ProImg }];
  }, [images]);

  const hasMultipleSlides = gallerySlides.length > 1;
  const paginationRef = useRef<HTMLDivElement>(null);

  return (
    <>
      {productDetail.loading ? <FrontLoader /> : null}
      <main>
        <div className="head-space">
          <div className="footer-space">
            <div className="container product-detail-page">
              <h1 className="sec-ttl mt-4 mb-3 text-capitalize">
                {LanguageConstants?.DASH_TRENDING_TITLE ??
                  LanguageConstants?.FOOTER_TRENDING_PRODUCTS ??
                  'Trending Products'}
              </h1>

              <div className="row">
                <div className="col-12">
                  {productDetail.status ? (
                    <article className="product-detail-card">
                      <div className="product-detail-card__gallery pro-sugg-img">
                        <div className="product-detail-card__swiper-media">
                          <Swiper
                            className="product-detail-card__swiper"
                            modules={[Pagination]}
                            pagination={{
                              clickable: true,
                              el: paginationRef.current,
                            }}
                            onBeforeInit={(swiper: SwiperType) => {
                              if (paginationRef.current) {
                                swiper.params.pagination = {
                                  ...(swiper.params.pagination as object),
                                  el: paginationRef.current,
                                };
                              }
                            }}
                            loop={hasMultipleSlides}
                            slidesPerView={1}
                            spaceBetween={0}
                            key={gallerySlides.map((s) => s.key).join('-')}
                          >
                            {gallerySlides.map((slide) => (
                              <SwiperSlide key={slide.key}>
                                <img src={slide.src} alt={productName} />
                              </SwiperSlide>
                            ))}
                          </Swiper>
                        </div>
                        <div
                          ref={paginationRef}
                          className="product-detail-card__swiper-pagination swiper-pagination"
                        />
                      </div>

                      <div className="pro-sugg-ct product-detail-card__body">
                        <h2 className="pro-sugg-ttl product-detail-card__title">
                          {productName}
                        </h2>
                        <h3 className="product-detail-card__descLabel">
                          {LanguageConstants?.Description ?? 'Description'}
                        </h3>
                        <p className="pro-sugg-para product-detail-card__desc">
                          {productDescription || '—'}
                        </p>
                      </div>
                    </article>
                  ) : !productDetail.loading ? (
                    <p className="product-detail-card__empty">
                      {LanguageConstants?.DATANOTFOUND}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ProductDetails;
